import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, ComponentSync, openContent, stopPropagation } from '@nu-art/thunderstorm-frontend';
import { capitalizeFirstLetter, cloneObj, compare, exists, generateHex } from '@nu-art/ts-common';
import { DBDef_Expression, DBDef_Variable, LogicType_AboveNormal, LogicType_BelowNormal, LogicType_Delta, LogicType_DeltaEquals, LogicType_DeltaGreaterEquals, LogicType_DeltaGreaterThan, LogicType_DeltaIn, LogicType_DeltaLesserEquals, LogicType_DeltaLesserThan, LogicType_DeltaPercentage, LogicType_DeltaPercentageEquals, LogicType_DeltaPercentageGreaterEquals, LogicType_DeltaPercentageGreaterThan, LogicType_DeltaPercentageIn, LogicType_DeltaPercentageLesserEquals, LogicType_DeltaPercentageLesserThan, LogicType_Equals, LogicType_Exists, LogicType_GreaterOrEqual, LogicType_GreaterThan, LogicType_In, LogicType_InNormal, LogicType_InSet, LogicType_IsChiefComplaint, LogicType_LessOrEquals, LogicType_LessThan, LogicType_NotEquals, LogicType_NotExists, LogicType_NotInSet, LogicType_Self, resolveValueDisplay, variableType_AttributeInstance } from '@app/hcs-shared';
import './Component_PrettyPrint_Atom.scss';
import { ICONS } from '@app/styles';
import { PPDescription, PPIndicatorStatus } from '../Component_PrettyPrint_Description/Component_PrettyPrint_Description.js';
const caseTooltipConfigResolver = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
        offset: { x: 16, y: -4 },
        modalPos: { x: 1, y: -1 },
        originPos: { x: rect.x, y: rect.y },
    };
};
export class Component_PrettyPrint_Atom extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(props, state) {
        state.atom = props.atom ? cloneObj(props.atom) : undefined;
        state.atomIndex = props.atomIndex;
        state.atomToVarMap = props.atomToVarMap;
        //Interactiveness
        state.interactive = props.interactive;
        if ('interactiveProperties' in props) {
            state.calculationResult = props.interactiveProperties.calculationResult;
            state.actualValue = props.interactiveProperties.actualValue;
            state.suggestedValue = props.interactiveProperties.suggestedValue;
            state.selectedVariableId = props.interactiveProperties.selectedVariableId;
            state.partialValue = props.interactiveProperties.partialValue;
        }
        else {
            delete state.calculationResult;
            delete state.actualValue;
            delete state.suggestedValue;
            delete state.selectedVariableId;
        }
        return state;
    }
    shouldReDeriveState(nextProps) {
        return super.shouldReDeriveState(nextProps)
            || !compare(nextProps.atom, this.state?.atom);
    }
    //######################### Logic #########################
    resolveLeftHandData = (atom) => {
        if (!atom.leftHand?.id)
            return;
        switch (atom.leftHand.type) {
            case 'variable': {
                const variable = this.props.modules.variable.cache.unique(atom.leftHand.id);
                return { variable };
            }
            case 'expression': {
                const expression = this.props.modules.expression.cache.unique(atom.leftHand.id);
                const variable = this.props.modules.variable.cache.unique(expression?.associatedVar);
                return { expression, variable };
            }
            case 'atom': {
                const id = Number(atom.leftHand.id) - 1;
                const mappedVar = this.state.atomToVarMap?.[id];
                switch (mappedVar?.type) {
                    case DBDef_Variable.dbKey: {
                        const variable = this.props.modules.variable.cache.unique(mappedVar.id);
                        return { variable };
                    }
                    case DBDef_Expression.dbKey: {
                        const expression = this.props.modules.expression.cache.unique(mappedVar.id);
                        const variable = this.props.modules.variable.cache.unique(expression?.associatedVar);
                        return { expression, variable };
                    }
                    default:
                        return;
                }
            }
            default:
                return;
        }
    };
    passingCaseIndex = (cases) => {
        const result = this.state.calculationResult;
        //Atom calculation result should be a number
        switch (typeof result) {
            case 'boolean': //Atom resolved into a boolean, if it's true the only case passed
                return result ? 0 : undefined;
            case 'number': //Atom resolved into a number, match case with actual value
                return cases.findIndex(c => c.value?.[0] === result);
        }
    };
    selectVariable = (e, variableId) => {
        stopPropagation(e);
        if (!('interactiveProperties' in this.props) || !variableId)
            return;
        this.props.interactiveProperties.onVariableSelected(variableId);
    };
    getCaseMeta = (variableId) => {
        const needsInput = this.state.interactive && !exists(this.state.actualValue);
        const hasSuggestion = this.state.interactive && needsInput && exists(this.state.suggestedValue);
        const hasPartialValue = this.state.interactive && needsInput && exists(this.state.partialValue);
        const selected = this.state.interactive && exists(this.state.selectedVariableId) && exists(variableId) && this.state.selectedVariableId === variableId;
        const toRet = { needsInput, hasSuggestion, selected, tooltipProps: {} };
        if (!needsInput && !hasSuggestion)
            return toRet;
        else if (hasSuggestion) {
            let content;
            switch (typeof this.state.suggestedValue) {
                case 'number':
                    content = _jsxs(_Fragment, { children: ["Suggested value is ", _jsx("b", { children: this.state.suggestedValue }), ", click to assign value"] });
                    break;
                case 'string': {
                    const value = this.props.modules.value.cache.unique(this.state.suggestedValue);
                    if (!value)
                        return toRet;
                    content = _jsxs(_Fragment, { children: ["Suggested value is ", _jsx("b", { children: resolveValueDisplay(value, true) }), ", click to assign value"] });
                }
            }
            toRet.tooltipProps = openContent.tooltip.custom('case-tooltip', content, caseTooltipConfigResolver);
        }
        else if (hasPartialValue) {
            toRet.tooltipProps = openContent.tooltip.custom('case-tooltip', _jsxs(_Fragment, { children: ["Value is ", _jsxs("b", { children: ["\u2265 ", this.state.partialValue] })] }), caseTooltipConfigResolver);
        }
        else
            toRet.tooltipProps = openContent.tooltip.custom('case-tooltip', 'Click to assign value', caseTooltipConfigResolver);
        return toRet;
    };
    getCaseVariableTooltip = () => {
        const needsInput = this.state.interactive && !exists(this.state.actualValue);
        const hasSuggestion = this.state.interactive && exists(this.state.suggestedValue) && needsInput;
        if (needsInput || hasSuggestion)
            return {};
        switch (typeof this.state.actualValue) {
            case 'number':
                return openContent.tooltip.custom('case-tooltip', _jsx("b", { children: this.state.actualValue }), caseTooltipConfigResolver);
            case 'string': {
                const value = this.props.modules.value.cache.unique(this.state.actualValue);
                if (!value)
                    return {};
                return openContent.tooltip.custom('case-tooltip', _jsx("b", { children: resolveValueDisplay(value, true) }), caseTooltipConfigResolver);
            }
            default:
                return {};
        }
    };
    getVarName = (variable) => {
        switch (variable?.type) {
            case variableType_AttributeInstance: {
                const nameParts = variable.name.split('.');
                return `${capitalizeFirstLetter(nameParts[0])} (${nameParts[1]})`;
            }
            default: {
                if (!variable)
                    return 'N/A';
                return capitalizeFirstLetter(variable.name);
            }
        }
    };
    getCaseClassname = (caseMeta, status) => {
        return _className('case-description', this.state.interactive && 'interactive', caseMeta.needsInput && 'needs-input', caseMeta.hasSuggestion && 'has-suggestion', caseMeta.selected && 'selected', status);
    };
    //######################### Render #########################
    render() {
        const atom = this.state.atom;
        if (!atom)
            return this.render_NotFound();
        const data = this.resolveLeftHandData(atom);
        if (atom.cases.length > 1 || exists(atom.fallbackValue))
            return this.render_Atom_MultiCase(atom.cases, atom.fallbackValue, data);
        //Single case atoms pass when the result is boolean => true
        const status = (typeof this.state.calculationResult === 'boolean')
            ? (this.state.calculationResult ? PPIndicatorStatus.pass : PPIndicatorStatus.fail)
            : (this.state.interactive ? PPIndicatorStatus.dataNeeded : undefined);
        return this.render_Case(atom.cases[0], data, status);
    }
    render_NotFound = () => {
        return _jsxs("div", { className: 'atom-pretty-print__not-found', children: ["A", this.state.atomIndex, ": Atom not found!"] });
    };
    //######################### Render - Atom Variants #########################
    render_Atom_MultiCase = (c, fallbackValue, data) => {
        const rows = [];
        const passingCaseIndex = this.passingCaseIndex(c);
        const fbValue = fallbackValue?.[0] ?? 0;
        const getCaseStatus = (caseIndex) => exists(passingCaseIndex)
            ? (passingCaseIndex === caseIndex
                ? PPIndicatorStatus.pass
                : PPIndicatorStatus.fail)
            : this.state.interactive ? PPIndicatorStatus.dataNeeded : undefined;
        //Special rendering for when the fallback value is 0 and there is only one case
        if (fbValue === 0 && c.length === 1) {
            const caseValue = c[0].value?.[0] ?? 0;
            const caseStatus = getCaseStatus(0);
            const atomClassName = _className('atom-description', 'single-case-with-value', caseStatus);
            return _jsx(PPDescription, { className: atomClassName, children: _jsxs(PPDescription.Row, { children: [_jsx(PPDescription.Indicator, { status: caseStatus }), this.render_Case(c[0], data, getCaseStatus(0)), _jsx(PPDescription.Value, { children: caseValue })] }) });
        }
        //Regular rendering
        c.forEach(((_c, index) => {
            const status = getCaseStatus(index);
            rows.push(_jsxs(PPDescription.Row, { children: [_jsx(PPDescription.TreeLineConnector, {}), this.render_Case(_c, data, status), _jsx(PPDescription.Value, { children: _c.value?.[0] ?? 0 })] }));
        }));
        const numericValue = typeof this.state.calculationResult === 'number' ? this.state.calculationResult : undefined;
        const atomStatus = this.state.interactive
            ? (exists(numericValue)
                ? PPIndicatorStatus.pass
                : PPIndicatorStatus.dataNeeded)
            : undefined;
        const atomClassName = _className('atom-description', atomStatus);
        return _jsxs(PPDescription, { className: atomClassName, children: [_jsxs(PPDescription.Row, { children: [_jsx(PPDescription.Indicator, { status: atomStatus }), _jsx(PPDescription.Title, { children: "One of:" }), _jsx(PPDescription.TitleValue, { value: numericValue, show: this.state.interactive })] }), ...rows, fbValue !== 0 && _jsxs(PPDescription.Row, { children: [_jsx(PPDescription.TreeLineConnector, {}), this.render_Case_Otherwise(getCaseStatus(-1)), _jsx(PPDescription.Value, { children: fbValue })] })] });
    };
    //######################### Render - Case #########################
    render_Case = (c, leftHandData, status) => {
        const variableId = leftHandData?.variable?._id;
        const caseMeta = this.getCaseMeta(variableId);
        const className = this.getCaseClassname(caseMeta, status);
        return _jsx(PPDescription, { className: className, children: _jsxs(PPDescription.Row, { children: [_jsx(PPDescription.Indicator, { status: status }), _jsxs("p", { className: 'case-description__content', ...caseMeta.tooltipProps, onClick: e => {
                            if (caseMeta.needsInput || caseMeta.hasSuggestion)
                                this.selectVariable(e, variableId);
                        }, children: [this.render_Case_Variable(leftHandData), ' ' + this.render_Case_Operator(c) + ' ', this.render_Case_Values(c), _jsx(ICONS.advisor_v5_plus.component, { asSpan: true, onClick: e => this.selectVariable(e, variableId) })] })] }) });
    };
    render_Case_Variable = (leftHandData) => {
        const variableName = this.getVarName(leftHandData?.variable);
        const expressionName = leftHandData?.expression?.label;
        return _jsx("span", { className: 'case-description__variable', onClick: e => this.selectVariable(e, leftHandData?.variable?._id), ...this.getCaseVariableTooltip(), children: expressionName ?? variableName });
    };
    render_Case_Operator = (c) => {
        switch (c.caseOperator) {
            //Operators that return nothing
            case LogicType_Equals:
            case LogicType_Self:
                return '';
            case LogicType_NotEquals:
                return 'is not';
            case LogicType_InSet:
                return 'is either:';
            case LogicType_NotInSet:
                return 'is neither:';
            case LogicType_In:
                return 'between';
            case LogicType_LessThan:
            case LogicType_GreaterThan:
            case LogicType_LessOrEquals:
            case LogicType_GreaterOrEqual:
                return c.caseOperator;
            case LogicType_AboveNormal:
            case LogicType_BelowNormal: {
                const operator = c.caseOperator === LogicType_AboveNormal ? 'above' : 'below';
                const factor = c.caseOperator === LogicType_AboveNormal ? c.factor?.upper : c.factor?.lower;
                if (factor && factor !== 1)
                    return `${operator} ${factor}x normal range`;
                return `${operator} normal range`;
            }
            case LogicType_InNormal: {
                const lowerF = c.factor?.lower ?? 1;
                const upperF = c.factor?.upper ?? 1;
                if (lowerF === 1 && upperF === 1)
                    return 'within normal range';
                return `within ${lowerF}x-${upperF}x normal range`;
            }
            case LogicType_Exists:
                return 'has a value';
            case LogicType_NotExists:
                return 'has no value';
            case LogicType_IsChiefComplaint:
                return 'is a chief complaint';
            //Delta logic types
            case LogicType_Delta:
            case LogicType_DeltaIn:
            case LogicType_DeltaEquals:
                return 'change of';
            case LogicType_DeltaGreaterThan:
                return 'change of more than';
            case LogicType_DeltaGreaterEquals:
                return 'change of at least';
            case LogicType_DeltaLesserThan:
                return 'change of less than';
            case LogicType_DeltaLesserEquals:
                return 'change of up to';
            //Percentage Delta logic types
            case LogicType_DeltaPercentage:
            case LogicType_DeltaPercentageIn:
            case LogicType_DeltaPercentageEquals:
                return 'percentage change of';
            case LogicType_DeltaPercentageGreaterThan:
                return 'percentage change of more than';
            case LogicType_DeltaPercentageGreaterEquals:
                return 'percentage change of at least';
            case LogicType_DeltaPercentageLesserThan:
                return 'percentage change of less than';
            case LogicType_DeltaPercentageLesserEquals:
                return 'percentage change of up to';
            default:
                return `Unhandled operator: ${c.caseOperator}`;
        }
    };
    render_Case_Values = (c) => {
        if (!c.rightHand)
            return;
        //Case 1: Range ([number,number])
        if (typeof c.rightHand[0] === 'number') {
            //Assume that rightHand[1] is also a number
            if (c.rightHand[0] === c.rightHand[1])
                return String(c.rightHand[0]);
            return `${String(c.rightHand[0])}-${String(c.rightHand[1])}`;
        }
        const actualValue = this.state.actualValue;
        let suggestedValue = exists(actualValue) ? undefined : this.state.suggestedValue;
        //Case 2: Array of ids
        return c.rightHand
            .map(id => this.props.modules.value.cache.unique(id))
            .map((val, index) => {
            const key = exists(val) ? val._id : generateHex(4);
            let value = exists(val) ? resolveValueDisplay(val, true) : 'N/A';
            const className = _className('case-description__value', index === 0 && 'first', index === c.rightHand.length - 1 && 'last', (exists(actualValue) && val?._id === actualValue) && 'selected', (exists(suggestedValue) && val?._id === suggestedValue) && 'suggested');
            if (index !== c.rightHand.length - 1)
                value += ',';
            return _jsx("span", { className: className, children: value }, key);
        });
    };
    render_Case_Otherwise = (status) => {
        const className = _className('case-description', status);
        return _jsx(PPDescription, { className: className, children: _jsxs(PPDescription.Row, { children: [_jsx(PPDescription.Indicator, { status: status }), _jsx("p", { className: 'case-description__content', children: "Otherwise:" })] }) });
    };
}
//# sourceMappingURL=Component_PrettyPrint_Atom.js.map